import React, { useState } from "react"
import * as deepmerge from "deepmerge"
import MDSpinner from "react-md-spinner"
import { Formik } from "formik"
import styles from "./TestDriveBookingFlow.module.sass"
import CarSummary from "./CarSummary"
import TradeInResult from "../TradeInResult"
import ContentBelowValuation from "../ContentBelowValuation"

// TODO: Update any to specific types
interface TestDriveLeadFormProps {
  leadInfo: any
  enableDescriptionOfTheCar: boolean
  enableOwnValuation: boolean
  extraFields: any
  customFields: any[]
  translations: any
  department: any[]
  modelList: any[]
  leadFormUrl: string
  carAssemblageData: any
  carInfo: any
  trackFunction: (string) => void
  creationToken: string
  tradeInFlowSelectorOptions: any
  bookingSummaryTranslations: any
  validationTranslations: any
  maxTradeInValue: number
  extraLeadQualification: any
  bookingTime: string
  vinLpn: string
  sendPicturesSmsProps: any
  configKey: string
  configParam: string
}

const TestDriveLeadForm = (props: TestDriveLeadFormProps): JSX.Element => {
  const [finalized, setFinalized] = useState<boolean>(false)
  const [validated, setValidated] = useState<boolean>(false)
  const [result, setResult] = useState({})

  const generateFinishedPayload = (values) => {
    const payload = {
      creation_token: props.creationToken,
      trade_in_valuation: {
        vin_lpn: props.vinLpn,
        extra_lead_qualification: { ...props.extraLeadQualification },
        extra_fields: {},
      },
    }

    const customFieldsNames = props.customFields.map((f) => f.fields.field_name)

    Object.keys(values).map((key) => {
      if (Object.keys(props.extraFields).includes(key)) {
        payload["trade_in_valuation"]["extra_lead_qualification"][`${key}`] =
          values[key]
      } else if (customFieldsNames.includes(key)) {
        payload["trade_in_valuation"]["extra_fields"][`${key}`] = values[key]
      } else {
        payload[`${key}`] = values[key]
      }
    })
    return payload
  }

  const leadFormDone = (payload) => {
    const headers = new Headers({
      "Content-Type": "application/json",
    })
    // Merge payload objects with initial payload, making sure they don't override each other
    payload = deepmerge.all([props.carAssemblageData, payload])
    payload[props.configParam] = props.configKey
    fetch(props.leadFormUrl, {
      method: "POST",
      body: JSON.stringify(payload),
      headers,
    })
      .then((res) => {
        return res.json()
      })
      .then((data) => {
        console.log(data)
        setResult(data.resultProps)
        setFinalized(true)
      })
    if (props.trackFunction)
      props.trackFunction("submitted the test drive lead form")
  }

  const { ...extraFields } = props.extraFields

  const initialValues = {}
  Object.keys(extraFields).forEach((field) => (initialValues[`${field}`] = ""))

  const requiredCustomFields = props.customFields.filter(
    (field) => field.fields.is_required
  )

  requiredCustomFields.forEach(
    (field) => (initialValues[field.fields.field_name] = "")
  )

  const refs = Object.keys(initialValues).reduce((acc, value) => {
    acc[value] = React.createRef()
    return acc
  }, {})

  const scrollToError = (id) => {
    refs[id].current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  return (
    <React.Fragment>
      {!finalized ? (
        <div className={styles.container}>
          <h1>{props.translations.headline}</h1>
          <div className="lead-form-wrapper form-visible">
            <div className="lead-form cta-block">
              <h2>{props.translations.subheadline}</h2>
              <br />
              <p>{props.translations.body}</p>
              <br />
            </div>
          </div>
          <CarSummary
            vinLpn={props.vinLpn?.toUpperCase()}
            brand={props.carInfo.brand}
            model={props.carInfo.car_model}
            year={props.carInfo.year}
            equipmentVariant={props.carInfo.equipment_variant}
            body={props.carInfo.body}
            engine={`${props.carInfo.engine_size} ${props.carInfo.fuel} (${props.carInfo.hp} ${props.translations.hp})`}
            gear={
              props.carInfo.has_auto_gear
                ? props.translations.automaticGears
                : props.translations.manualGears
            }
            yourCar={props.translations.yourCar}
          />
          <Formik
            validateOnChange={validated}
            validateOnBlur={validated}
            initialValues={initialValues}
            validate={(values, touched) => {
              const errors = {}
              Object.keys(extraFields).map((field) => {
                if (!values[`${field}`]) {
                  errors[`${field}`] = props.validationTranslations.required
                }
              })

              requiredCustomFields.map((field) => {
                if (!values[`${field.fields.field_name}`]) {
                  errors[`${field.fields.field_name}`] =
                    props.validationTranslations.required
                }
              })

              if (values.buyer_self_valuation > props.maxTradeInValue) {
                errors.buyer_self_valuation =
                  props.validationTranslations.maxTradeInValue
              }

              if (Object.keys(errors).length > 0) {
                scrollToError(Object.keys(errors)[0])
              }
              setValidated(true)

              return errors
            }}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                console.log(values)
                console.log(generateFinishedPayload(values))
                leadFormDone(generateFinishedPayload(values))
                setSubmitting(false)
              }, 400)
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isValidating,
            }) => (
              <form onSubmit={handleSubmit}>
                {Object.keys(extraFields).map((field) => {
                  return (
                    <div className="form-row" key={field}>
                      <label htmlFor={field}>
                        {
                          props.translations.extraLeadQualificationTranslations[
                            field
                          ]
                        }
                      </label>
                      {extraFields[field] === "freetext" ||
                      extraFields[field] === "vin" ? (
                        <React.Fragment>
                          <input
                            type="text"
                            id={field}
                            name={field}
                            ref={refs[field]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={
                              errors[`${field}`] && touched[`${field}`]
                                ? "invalid"
                                : values[`${field}`] && touched[`${field}`]
                                ? "valid"
                                : ""
                            }
                          />
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <div
                            className={`radio-button-group-${extraFields[field].length} obvious horizontal`}
                          >
                            {extraFields[field].map((opt, i) => {
                              return (
                                <label
                                  key={i}
                                  className={`btn-grouped themed_v2 ${
                                    values[field] === opt ? "active" : ""
                                  }`}
                                  htmlFor={field}
                                >
                                  <input
                                    name={field}
                                    id={field}
                                    ref={refs[field]}
                                    onChange={handleChange}
                                    type="radio"
                                    value={opt}
                                    checked={values[`${field}`] === opt}
                                  />
                                  {opt === "false"
                                    ? props.translations.no
                                    : props.translations.yes}
                                </label>
                              )
                            })}
                          </div>
                        </React.Fragment>
                      )}

                      <div className="invalid">
                        {errors[`${field}`] &&
                          touched[`${field}`] &&
                          errors[`${field}`]}
                      </div>
                    </div>
                  )
                })}
                {props.customFields.map((field) => {
                  return (
                    <div className="form-row" key={field.fields.field_name}>
                      <label htmlFor={field.fields.field_name}>
                        {field.fields.label}
                      </label>
                      {props.modelList &&
                      field.fields.field_name === "interested_in_car_model" ? (
                        <select
                          id={field.fields.field_name}
                          onChange={handleChange}
                          ref={refs[field.fields.field_name]}
                          name={field.fields.field_name}
                          className="Dropdown-control select-department full-width"
                          value={values[field.fields.field_name]}
                        >
                          {props.modelList.map((option) => (
                            <option value={option[1]} key={option[1]}>
                              {option[0]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          id={field.fields.field_name}
                          name={field.fields.field_name}
                          ref={refs[field.fields.field_name]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={
                            errors[`${field.fields.field_name}`] &&
                            touched[`${field.fields.field_name}`]
                              ? "invalid"
                              : values[`${field.fields.field_name}`] &&
                                touched[`${field.fields.field_name}`]
                              ? "valid"
                              : ""
                          }
                        />
                      )}
                      <div className="invalid">
                        {errors[`${field.fields.field_name}`] &&
                          touched[`${field.fields.field_name}`] &&
                          errors[`${field.fields.field_name}`]}
                      </div>
                    </div>
                  )
                })}
                {props.enableDescriptionOfTheCar && (
                  <div className="form-row">
                    <label htmlFor="buyer_comments">
                      {props.translations.labels.buyer_comments}
                    </label>
                    <textarea
                      name="buyer_comments"
                      id="buyer_comments"
                      placeholder={
                        props.translations.labels.buyer_comments_placeholder
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="full-width"
                    />
                  </div>
                )}
                {props.enableOwnValuation && (
                  <div className="form-row">
                    <label htmlFor="buyer_self_valuation">
                      {props.translations.labels.buyer_self_valuation}
                    </label>
                    <div className="input-with-label left">
                      <input
                        type="number"
                        id="buyer_self_valuation"
                        name="buyer_self_valuation"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={
                          errors.buyer_self_valuation &&
                          touched.buyer_self_valuation
                            ? "invalid"
                            : values.buyer_self_valuation &&
                              touched.buyer_self_valuation
                            ? "valid"
                            : ""
                        }
                      />
                      <span className="label">
                        {props.translations.currency}
                      </span>
                    </div>
                    <div className="invalid">
                      {errors.buyer_self_valuation &&
                        touched.buyer_self_valuation &&
                        errors.buyer_self_valuation}
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <button
                    type="submit"
                    className="main-cta"
                    onClick={handleSubmit}
                  >
                    {isValidating || finalized ? (
                      <MDSpinner
                        singleColor="#fff"
                        size={18}
                        className={styles.buttonSpinner}
                      />
                    ) : (
                      <span>{props.translations.cta}</span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      ) : (
        <span>
          <TradeInResult
            {...result.tradeInResult}
            carSummaryHtml={result.carSummaryHtml}
            sendPicturesSmsProps={result.sendPicturesSmsProps}
            bookingSummaryProps={{
              tradeInValuation: props.leadInfo,
              department: props.department,
              showHeadline: false,
              translations: props.bookingSummaryTranslations,
              bookingTime: props.bookingTime,
            }}
            trackFunction={props.trackFunction}
          />
          <ContentBelowValuation
            {...{
              ...result.contentBelowValuation,
              ...props.tradeInFlowSelectorOptions,
            }}
            trackFunction={props.trackFunction}
          />
        </span>
      )}
    </React.Fragment>
  )
}

export default TestDriveLeadForm
